import { Link } from 'react-router-dom';
import type { AgentCardData } from '@command-room/shared';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/cn';
import { relativeTime } from '@/lib/format';
import { initialsOf } from '@/lib/initials';

export const STATUS_STYLES: Record<AgentCardData['status_badge'], string> = {
  active: 'bg-success/15 text-success border-success/30',
  busy: 'bg-warning/20 text-warning-foreground border-warning/40',
  scheduled: 'bg-primary/15 text-primary border-primary/30',
  onboarding: 'bg-secondary text-secondary-foreground border-secondary',
  inactive: 'bg-muted text-muted-foreground border-muted',
};

export function AgentCard({ agent }: { agent: AgentCardData }) {
  const initials = initialsOf(agent.name);
  return (
    <Link
      to={`/agents/${encodeURIComponent(agent.name)}`}
      className="group block"
    >
      <Card className="flex h-full flex-col gap-3 p-4 transition-all hover:border-primary/40 hover:shadow-md">
        <div className="flex items-start gap-3">
          <Avatar className="h-12 w-12 ring-1 ring-border">
            {agent.agent_icon_url ? (
              <AvatarImage src={agent.agent_icon_url} alt={agent.name} />
            ) : null}
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <h3 className="truncate font-semibold leading-tight">{agent.name}</h3>
              <span
                className={cn(
                  'rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider',
                  STATUS_STYLES[agent.status_badge],
                )}
              >
                {agent.status_badge}
              </span>
            </div>
            <p className="mt-0.5 truncate text-xs text-muted-foreground">
              {agent.role ?? 'No role'}
            </p>
          </div>
        </div>
        <div className="mt-auto flex items-center justify-between text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <Badge variant={agent.type === 'AI' ? 'secondary' : 'outline'} className="px-1.5 py-0 text-[10px]">
              {agent.type ?? '—'}
            </Badge>
            {agent.department ?? '—'}
          </span>
          <span>{relativeTime(agent.last_active_at)}</span>
        </div>
      </Card>
    </Link>
  );
}
